import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

/**
 * Reusable AI Disclosure components for The Authority Gap / AuthorityOS ecosystem.
 *
 * - <AIDisclosure inline /> — appended to AI-generated content blocks
 * - <AIAssistantDisclosure /> — persistent panel above an AI assistant chat surface
 * - <FooterDisclosure /> — compact line for site footers
 *
 * All copy matches the AI Disclosure Policy at /legal/ai-disclosure and is kept in
 * sync with the standard ecosystem language.
 */

export const AIDisclosure = ({ inline = false, compact = false, className = "", style = {} }) => {
  const body =
    "This response was generated with the assistance of artificial intelligence and is intended solely for " +
    "educational and informational purposes. Artificial intelligence systems may produce inaccurate, " +
    "incomplete, outdated, or incorrect information. Users are responsible for independently evaluating all " +
    "information and consulting qualified professionals before making decisions based upon any information " +
    "provided. The Authority Gap makes no guarantee regarding accuracy, completeness, legality, suitability, " +
    "or fitness for any specific purpose.";

  if (compact) {
    return (
      <div
        data-testid="ai-disclosure-compact"
        className={className}
        style={{
          fontSize: 10,
          color: "#777",
          lineHeight: 1.5,
          fontStyle: "italic",
          padding: "6px 0",
          ...style,
        }}
      >
        AI-assisted output. Educational only.{" "}
        <Link to="/legal/ai-disclosure" style={{ color: "#FF6575" }}>Read the AI Disclosure</Link>
      </div>
    );
  }

  return (
    <aside
      role="note"
      data-testid={inline ? "ai-disclosure-inline" : "ai-disclosure-standalone"}
      className={className}
      style={{
        background: "#0d0d0d",
        border: "1px solid #1a1a1a",
        borderLeft: "3px solid #FF6575",
        borderRadius: 8,
        padding: 14,
        marginTop: inline ? 12 : 0,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Sparkles size={13} color="#FF6575" />
        <span style={{ color: "#FF6575", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          AI Disclosure
        </span>
      </div>
      <p style={{ color: "#aaa", fontSize: 11, lineHeight: 1.65, margin: 0 }}>{body}</p>
      <div style={{ marginTop: 8 }}>
        <Link to="/legal/ai-disclosure" style={{ color: "#777", fontSize: 10 }}>
          Full AI Disclosure Policy →
        </Link>
      </div>
    </aside>
  );
};

export const AIAssistantDisclosure = ({ assistantName = "this AI assistant", style = {} }) => (
  <aside
    role="note"
    data-testid="ai-assistant-disclosure"
    style={{
      background: "#0d0d0d",
      border: "1px solid #1a1a1a",
      borderLeft: "3px solid #FF6575",
      borderRadius: 8,
      padding: 14,
      ...style,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <Sparkles size={13} color="#FF6575" />
      <span style={{ color: "#FF6575", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        AI Assistant Notice
      </span>
    </div>
    <p style={{ color: "#aaa", fontSize: 11, lineHeight: 1.65, margin: 0 }}>
      The information provided by {assistantName} is educational and informational only. {assistantName} is not a lawyer,
      doctor, therapist, accountant, engineer, financial advisor, tax advisor, regulator, auditor, compliance
      officer, or licensed professional. Do not rely on this information as a substitute for professional
      advice. Always consult appropriately qualified professionals regarding your specific circumstances.
    </p>
    <div style={{ marginTop: 8, display: "flex", gap: 14 }}>
      <Link to="/legal/ai-use" style={{ color: "#777", fontSize: 10 }}>AI Use Policy →</Link>
      <Link to="/legal/ai-disclosure" style={{ color: "#777", fontSize: 10 }}>AI Disclosure →</Link>
    </div>
  </aside>
);

export const FooterDisclosure = () => (
  <div
    data-testid="footer-disclosure"
    style={{
      maxWidth: 1100,
      margin: "0 auto",
      padding: "14px 0 0",
      color: "#666",
      fontSize: 10,
      lineHeight: 1.65,
    }}
  >
    The Authority Gap provides educational frameworks, templates, software, and AI assistants. Nothing
    published here constitutes legal, medical, mental health, tax, financial, investment, insurance,
    regulatory, accreditation, or other professional advice. AuthorityOS is an educational operating
    framework — not a certification, accreditation, or compliance framework. AI-generated content may be
    inaccurate; verify independently and consult qualified professionals. See our{" "}
    <Link to="/legal/disclaimer" style={{ color: "#888" }}>General Disclaimer</Link>,{" "}
    <Link to="/legal/earnings-disclaimer" style={{ color: "#888" }}>Earnings Disclaimer</Link>, and{" "}
    <Link to="/legal/authorityos-disclaimer" style={{ color: "#888" }}>AuthorityOS Disclaimer</Link>.
  </div>
);
